import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  FileText,
  CheckCircle,
  AlertTriangle,
  Plus,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const CreateTimesheet = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    employee: '',
    department: '',
    weekStarting: '',
    weekEnding: '',
    status: 'draft',
    
    // Time Entries
    timeEntries: [],
    
    // Summary
    totalHours: 0,
    billableHours: 0,
    nonBillableHours: 0,
    
    // Additional Details
    notes: '',
    submittedAt: null,
    approvedBy: '',
    approvedAt: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeDayTab, setActiveDayTab] = useState(0);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsResponse, membersResponse] = await Promise.all([
          fetch(API_CONFIG.ENDPOINTS.PROJECTS, { headers: API_CONFIG.HEADERS }),
          fetch(API_CONFIG.ENDPOINTS.TEAM_MEMBERS, { headers: API_CONFIG.HEADERS })
        ]);
        
        if (projectsResponse.ok) {
          const projectJson = await projectsResponse.json();
          
          const rawProjects = projectJson?.data?.projects || [];
          setProjects(rawProjects);

          console.log(projectJson);
        }
        
        if (membersResponse.ok) {
          const membersJson = await membersResponse.json();
          setEmployees(membersJson.data || []);
        }
      } catch (err) {
        showError('Failed to load projects or team members');
      }
    };

    loadData();
    
    // Set default week (current week)
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    setFormData(prev => ({
      ...prev,
      weekStarting: startOfWeek.toISOString().split('T')[0],
      weekEnding: endOfWeek.toISOString().split('T')[0]
    }));
  }, []);

  // Initialize time entries for the week
  useEffect(() => {
    if (formData.weekStarting && formData.weekEnding) {
      const entries = generateWeekEntries(formData.weekStarting, formData.weekEnding);
      setFormData(prev => ({
        ...prev,
        timeEntries: entries
      }));
    }
  }, [formData.weekStarting, formData.weekEnding]);

  useEffect(() => {
    if (!formData.employee && employees.length > 0) {
      const firstEmployee = employees[0];
      setFormData(prev => ({
        ...prev,
        employee: firstEmployee._id || firstEmployee.id,
        department: firstEmployee.department || prev.department
      }));
    }
  }, [employees, formData.employee]);

  // Generate time entries for each day of the week
  const generateWeekEntries = (startDate, endDate) => {
    const entries = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      entries.push({
        id: `day-${d.toISOString().split('T')[0]}`,
        date: d.toISOString().split('T')[0],
        dayName: d.toLocaleDateString('en-US', { weekday: 'long' }),
        entries: [
          {
            id: `entry-${d.toISOString().split('T')[0]}-1`,
            project: '',
            task: '',
            description: '',
            startTime: '09:00',
            endTime: '17:00',
            duration: 8,
            billable: true,
            hourlyRate: 75
          }
        ]
      });
    }
    
    return entries;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle time entry changes
  const handleTimeEntryChange = (dayId, entryId, field, value) => {
    setFormData(prev => ({
      ...prev,
      timeEntries: prev.timeEntries.map(day => 
        day.id === dayId 
          ? {
              ...day,
              entries: day.entries.map(entry => 
                entry.id === entryId 
                  ? { ...entry, [field]: value }
                  : entry
              )
            }
          : day
      )
    }));
  };

  // Add new time entry to a day
  const handleAddTimeEntry = (dayId) => {
    const newEntry = {
      id: `entry-${dayId}-${Date.now()}`,
      project: '',
      task: '',
      description: '',
      startTime: '09:00',
      endTime: '17:00',
      duration: 8,
      billable: true,
      hourlyRate: 75
    };

    setFormData(prev => ({
      ...prev,
      timeEntries: prev.timeEntries.map(day => 
        day.id === dayId 
          ? { ...day, entries: [...day.entries, newEntry] }
          : day
      )
    }));
  };

  // Remove time entry
  const handleRemoveTimeEntry = (dayId, entryId) => {
    setFormData(prev => ({
      ...prev,
      timeEntries: prev.timeEntries.map(day => 
        day.id === dayId 
          ? { ...day, entries: day.entries.filter(entry => entry.id !== entryId) }
          : day
      )
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalHours = 0;
    let billableHours = 0;
    let nonBillableHours = 0;

    formData.timeEntries.forEach(day => {
      day.entries.forEach(entry => {
        totalHours += parseFloat(entry.duration) || 0;
        if (entry.billable) {
          billableHours += parseFloat(entry.duration) || 0;
        } else {
          nonBillableHours += parseFloat(entry.duration) || 0;
        }
      });
    });

    setFormData(prev => ({
      ...prev,
      totalHours,
      billableHours,
      nonBillableHours
    }));
  };

  // Update totals when time entries change
  useEffect(() => {
    calculateTotals();
  }, [formData.timeEntries]);

  // Handle week navigation
  const handleWeekChange = (direction) => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + (direction * 7));
    setCurrentWeek(newWeek);
    
    const startOfWeek = new Date(newWeek);
    startOfWeek.setDate(newWeek.getDate() - newWeek.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    setFormData(prev => ({
      ...prev,
      weekStarting: startOfWeek.toISOString().split('T')[0],
      weekEnding: endOfWeek.toISOString().split('T')[0]
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const selectedEmployee = employees.find((member) => (member._id || member.id) === formData.employee);
      const projectMap = new Map(
  (Array.isArray(projects) ? projects : []).map(project => [
    project._id || project.id,
    project.name
  ])
);


      const payload = {
        employee: selectedEmployee?._id || selectedEmployee?.id,
        employeeName: selectedEmployee?.name || '',
        department: selectedEmployee?.department || formData.department,
        weekStarting: formData.weekStarting,
        weekEnding: formData.weekEnding,
        status: formData.status,
        notes: formData.notes,
        
        timeEntries: formData.timeEntries.map((day) => ({
        date: day.date,
        dayName: day.dayName,
        entries: day.entries.map((entry) => {
          const cleanEntry = {
            task: entry.task,
            description: entry.description,
            startTime: entry.startTime,
            endTime: entry.endTime,
            duration: Number(entry.duration) || 0,
            billable: entry.billable,
            hourlyRate: Number(entry.hourlyRate) || 0
          };
          
          if (entry.project) {
            cleanEntry.project = entry.project;
            cleanEntry.projectName = projectMap.get(entry.project) || '';
          }
          return cleanEntry;
        })
      }))
      };

      const response = await fetch(API_CONFIG.ENDPOINTS.TIMESHEETS, {
        method: 'POST',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create timesheet');
      }

      success('Timesheet created successfully!');
      navigate('/projects/time-tracking/timesheets');
    } catch (err) {
      console.error('Error creating timesheet:', err);
      showError(err.message || 'Failed to create timesheet. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Create Timesheet"
        description="Create and manage weekly timesheets for time tracking"
      />
      <div className="page-container">

      {/* Main Content */}
      <div className="project-create-container">
        <form onSubmit={handleSubmit}>
          {/* Form Card */}
          <div className="project-form-card">
            {/* Basic Information Section */}
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Timesheet Information</h3>
                <p className="section-description">Basic information about the timesheet</p>
              </div>
              
              {/* 4-Column Grid Layout */}
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Employee *</label>
                  <select
                    className="form-select"
                    value={formData.employee}
                    onChange={(e) => {
                      const value = e.target.value;
                      const selectedEmployee = employees.find((member) => (member._id || member.id) === value);
                      setFormData((prev) => ({
                        ...prev,
                        employee: value,
                        department: selectedEmployee?.department || prev.department
                      }));
                    }}
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map(employee => (
                      <option key={employee._id || employee.id} value={employee._id || employee.id}>
                        {employee.name} {employee.department ? `- ${employee.department}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Week Starting *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.weekStarting}
                    onChange={(e) => handleInputChange('weekStarting', e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Week Ending *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.weekEnding}
                    onChange={(e) => handleInputChange('weekEnding', e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="submitted">Submitted</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Week Navigation */}
              <div className="week-navigation">
                <div className="d-flex align-items-center justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline d-flex align-items-center"
                    onClick={() => handleWeekChange(-1)}
                  >
                    <ChevronLeft size={16} />
                    Previous Week
                  </button>
                  
                  <div className="week-info">
                    <h4 className="mb-0">
                      Week of {new Date(formData.weekStarting).toLocaleDateString()} - {new Date(formData.weekEnding).toLocaleDateString()}
                    </h4>
                  </div>
                  
                  <button
                    type="button"
                    className="btn btn-outline d-flex align-items-center"
                    onClick={() => handleWeekChange(1)}
                  >
                    Next Week
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Time Entries Section with Tabs */}
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Time Entries</h3>
                <p className="section-description">Log your daily time entries for the week</p>
              </div>
              
              {/* Day Tabs */}
              <div className="timesheet-tabs-container">
                <div className="timesheet-tabs">
                  {formData.timeEntries.map((day, index) => (
                    <button
                      key={day.id}
                      type="button"
                      className={`timesheet-tab ${activeDayTab === index ? 'active' : ''}`}
                      onClick={() => setActiveDayTab(index)}
                    >
                      <div className="tab-day-name">{day.dayName}</div>
                      <div className="tab-date">{new Date(day.date).getDate()}</div>
                    </button>
                  ))}
                </div>
                
                {/* Tab Content */}
                <div className="timesheet-tab-content">
                  {formData.timeEntries.map((day, dayIndex) => (
                    <div 
                      key={day.id} 
                      className={`timesheet-tab-panel ${activeDayTab === dayIndex ? 'active' : ''}`}
                    >
                      <div className="day-timesheet">
                        <div className="day-header">
                          <h5 className="day-title">
                            {day.dayName} - {new Date(day.date).toLocaleDateString()}
                          </h5>
                          <button
                            type="button"
                            className="btn btn-outline btn-sm d-flex align-items-center"
                            onClick={() => handleAddTimeEntry(day.id)}
                          >
                            <Plus size={16} />
                            Add Entry
                          </button>
                        </div>
                        
                        <div className="time-entries">
                          {day.entries.map((entry, index) => (
                            <div key={entry.id} className="time-entry-row">
                              <div className="row">
                                <div className="col-md-3">
                                  <div className="form-group">
                                    <label className="form-label">Project</label>
                                    <select
                                      className="form-select"
                                      value={entry.project}
                                      onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'project', e.target.value)}
                                    >
                                      <option value="">Select Project</option>
                                      {Array.isArray(projects) &&
                                      projects.map(project => (
                                      <option key={project._id || project.id} value={project._id || project.id}>
                                        {project.name}
                                      </option>
                                    ))}
                                    </select>
                                  </div>
                                </div>
                                
                                <div className="col-md-2">
                                  <div className="form-group">
                                    <label className="form-label">Start Time</label>
                                    <input
                                      type="time"
                                      className="form-control"
                                      value={entry.startTime}
                                      onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'startTime', e.target.value)}
                                    />
                                  </div>
                                </div>
                                
                                <div className="col-md-2">
                                  <div className="form-group">
                                    <label className="form-label">End Time</label>
                                    <input
                                      type="time"
                                      className="form-control"
                                      value={entry.endTime}
                                      onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'endTime', e.target.value)}
                                    />
                                  </div>
                                </div>
                                
                                <div className="col-md-1">
                                  <div className="form-group">
                                    <label className="form-label">Hours</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={entry.duration}
                                      onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'duration', e.target.value)}
                                      step="0.25"
                                      min="0"
                                    />
                                  </div>
                                </div>
                                
                                <div className="col-md-2">
                                  <div className="form-group">
                                    <label className="form-label">Rate ($)</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={entry.hourlyRate}
                                      onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'hourlyRate', e.target.value)}
                                      step="0.01"
                                      min="0"
                                    />
                                  </div>
                                </div>
                                
                                <div className="col-md-1">
                                  <div className="form-group">
                                    <label className="form-label">Billable</label>
                                    <div className="form-check">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={entry.billable}
                                        onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'billable', e.target.checked)}
                                      />
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="col-md-1">
                                  <div className="form-group">
                                    <label className="form-label">&nbsp;</label>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => handleRemoveTimeEntry(day.id, entry.id)}
                                      disabled={day.entries.length === 1}
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="row">
                                <div className="col-md-12">
                                  <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={entry.description}
                                      onChange={(e) => handleTimeEntryChange(day.id, entry.id, 'description', e.target.value)}
                                      placeholder="What did you work on?"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="project-form-section">
              <div className="section-header">
                <h3 className="section-title">Timesheet Summary</h3>
                <p className="section-description">Weekly totals and summary information</p>
              </div>
              
              <div className="form-grid-4">
                <div className="form-group">
                  <label className="form-label">Total Hours</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.totalHours}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Billable Hours</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.billableHours}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Non-Billable Hours</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.nonBillableHours}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Total Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={(formData.billableHours * 75).toFixed(2)}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Additional notes or comments about this timesheet..."
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn-outline-cam"
              onClick={() => navigate('/projects/time-tracking/timesheets')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-cam"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Creating Timesheet...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Create Timesheet
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </>
  );
};

export default CreateTimesheet;
